import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { isOwnerOrFinance } from "../../utils/role-check";
import "./user-list-modern.css";
import StyledModal from "../Modal/StyledModalView";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";

function UserListViewModern({
  users,
  loading,
  error,
  userDocuments,
  auth,
  onViewUser,
  onInviteUser,
  onDeleteUser,
  showDeleteModal,
  confirmDeleteUser,
  cancelDeleteUser
}) {
  const { t } = useTranslation();
  const [inviteVisible, setInviteVisible] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("zzper");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("name-asc");

  // Helper function to normalize document category for comparison
  const normalizeDocCategory = (category) => {
    if (!category) return '';
    return category.toLowerCase().replace(/[\s\-_]/g, '');
  };

  const getDocumentStatus = (documents) => {
    console.log('[USERLIST-MODERN] getDocumentStatus called');
    console.log('[USERLIST-MODERN] Documents received:', documents);
    console.log('[USERLIST-MODERN] Is array?', Array.isArray(documents));
    console.log('[USERLIST-MODERN] Length:', documents?.length);

    if (documents && documents.length > 0) {
      console.log('[USERLIST-MODERN] Sample document:', documents[0]);
      console.log('[USERLIST-MODERN] Document categories:', documents.map(d => d.document_category));
    }

    const requiredTypes = [
      { type: "vca diploma", normalized: "vcadiploma" },
      { type: "kvk-uittreksel", normalized: "kvkuittreksel" },
      { type: "verzekeringspolis", normalized: "verzekeringspolis" }
    ];
    const now = new Date();
    let hasMissing = false;
    let hasExpired = false;

    for (const req of requiredTypes) {
      const docs = (documents || []).filter(doc =>
        normalizeDocCategory(doc.document_category) === req.normalized
      );
      console.log(`[USERLIST-MODERN] Checking ${req.type}:`, docs.length, 'found');
      if (docs.length > 0) {
        console.log(`[USERLIST-MODERN] ${req.type} documents:`, docs.map(d => `${d.document_category} → ${normalizeDocCategory(d.document_category)}`));
      }
      if (docs.length === 0) {
        console.log(`[USERLIST-MODERN] MISSING: ${req.type}`);
        hasMissing = true;
        continue;
      }
      const hasValid = docs.some(doc => {
        const exp = doc.expires_at ? new Date(doc.expires_at) : null;
        if (!exp) return true;
        return exp >= now;
      });
      if (!hasValid) {
        console.log(`[USERLIST-MODERN] EXPIRED: ${req.type}`);
        hasExpired = true;
      }
    }

    const result = hasMissing ? "missing" : (hasExpired ? "expired" : "valid");
    console.log('[USERLIST-MODERN] Final status:', result);
    return result;
  };

  const handleInviteUser = async () => {
    const result = await onInviteUser(inviteEmail, inviteRole, setInviteEmail, setInviteRole);
    if (result !== false) setInviteVisible(false);
  };

  if (loading) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={t('common.error')}/>;

  // Filter users
  const filteredUsers = users
    .filter(user => isOwnerOrFinance(auth.role) || user.role === "zzper")
    .filter(user => {
      const q = search.toLowerCase();
      return (
        user.name?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q) ||
        (user.kvk_number || "").toString().includes(q)
      );
    })
    .filter(user => {
      if (roleFilter === "all") return true;
      return user.role === roleFilter;
    });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortOrder) {
      case "name-asc": return (a.name || "").localeCompare(b.name || "");
      case "name-desc": return (b.name || "").localeCompare(a.name || "");
      case "role-asc": return (a.role || "").localeCompare(b.role || "");
      case "role-desc": return (b.role || "").localeCompare(a.role || "");
      default: return 0;
    }
  });

  return (
    <div className="user-list-modern">
      {/* Header */}
      <div className="list-header">
        <div className="list-header-left">
          <h2>{t('users.title')}</h2>
          <span className="count-badge">{sortedUsers.length} {t('users.users')}</span>
        </div>
        {isOwnerOrFinance(auth.role) && (
          <button
            className="btn btn-add"
            onClick={() => setInviteVisible(true)}
          >
            {t('users.inviteUser')}
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="search"
            className="search-input"
            placeholder={t('users.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-btn" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        <div className="filters-right">
          <div className="filter-group form-group mb-0">
            <label htmlFor="role-filter">{t('users.role')}:</label>
            <select
              id="role-filter"
              className="filter-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              <option value="all">{t('common.all')}</option>
              <option value="eigenaar">{t('user.roles.eigenaar')}</option>
              <option value="financieel-medewerker">{t('user.roles.financieel-medewerker')}</option>
              <option value="klant">{t('user.roles.klant')}</option>
              <option value="voorman">{t('user.roles.voorman')}</option>
              <option value="zzper">{t('user.roles.zzper')}</option>
            </select>
          </div>

          <div className="filter-group form-group mb-0">
            <label htmlFor="sort">{t('common.sortBy')}:</label>
            <select
              id="sort"
              className="filter-select"
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
            >
              <option value="name-asc">{t('users.sortNameAsc')}</option>
              <option value="name-desc">{t('users.sortNameDesc')}</option>
              <option value="role-asc">{t('users.sortRoleAsc')}</option>
              <option value="role-desc">{t('users.sortRoleDesc')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      {sortedUsers.length === 0 ? (
        <NoDataConstruction text={t('users.noUsersFound')} />
      ) : (
        <div className="users-grid">
          {sortedUsers.map(user => {
            const docs = userDocuments[user.id] || [];

            console.log(`[USERLIST-MODERN] User ${user.name} (${user.id}):`, docs.length, 'documents');
            const docStatus = getDocumentStatus(docs);
            console.log(`[USERLIST-MODERN] User ${user.name} (${user.id}) final status: ${docStatus}`);

            return (
              <Link
                key={user.id}
                to={`/users/${user.id}`}
                className="user-card"
              >
                <div className="user-card-header">
                  <div className="user-avatar">
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt={user.name}
                        className="avatar-image"
                      />
                    ) : (
                      <span className="avatar-initials">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="user-info">
                    <h3>{user.name}</h3>
                    <span className="user-role-badge">{user.role}</span>
                  </div>
                </div>

                <div className="user-card-body">
                  {user.email && (
                    <div className="user-detail">
                      <span className="detail-label">{t('common.email')}:</span>
                      <span className="detail-value">{user.email}</span>
                    </div>
                  )}
                  {user.phone && (
                    <div className="user-detail">
                      <span className="detail-label">{t('common.phone')}:</span>
                      <span className="detail-value">{user.phone}</span>
                    </div>
                  )}
                  {user.company_name && (
                    <div className="user-detail">
                      <span className="detail-label">{t('common.company')}:</span>
                      <span className="detail-value">{user.company_name}</span>
                    </div>
                  )}
                </div>

                <div className="user-card-footer">
                  <span className={`status-badge status-${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? t('profile.active') : t('profile.inactive')}
                  </span>
                  <span className={`doc-badge doc-${docStatus}`}>
                    {docStatus === 'valid' ? t('documents.valid') :
                     docStatus === 'expired' ? t('documents.expired') :
                     t('documents.missing')}
                  </span>
                </div>

                {isOwnerOrFinance(auth.role) && (
                  <div className="user-card-actions">
                    <button
                      className="btn btn-delete"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteUser(user);
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* Invite User Modal */}
      {inviteVisible && (
        <StyledModal
          isOpen={inviteVisible}
          onClose={() => {
            setInviteVisible(false);
            setInviteEmail("");
            setInviteRole("zzper");
          }}
          title={t('users.inviteUser')}
        >
          <div className="modal-form">
            <div className="form-group">
              <label className="form-label required">{t('common.email')}</label>
              <input
                type="email"
                className="form-input"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('users.enterEmail')}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label required">{t('users.role')}</label>
              <select
                className="form-input"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="zzper">{t('user.roles.zzper')}</option>
                <option value="klant">{t('user.roles.klant')}</option>
                <option value="financieel-medewerker">{t('user.roles.financieel-medewerker')}</option>
                <option value="eigenaar">{t('user.roles.eigenaar')}</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => {
                  setInviteVisible(false);
                  setInviteEmail("");
                  setInviteRole("zzper");
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-save"
                onClick={handleInviteUser}
                disabled={!inviteEmail}
              >
                {t('users.sendInvite')}
              </button>
            </div>
          </div>
        </StyledModal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <StyledModal
          isOpen={showDeleteModal}
          onClose={cancelDeleteUser}
          title={t('common.confirmDelete')}
        >
          <p>{t('users.confirmDeleteMessage')}</p>
          <div className="modal-actions">
            <button className="btn btn-cancel" onClick={cancelDeleteUser}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-delete" onClick={confirmDeleteUser}>
              {t('common.delete')}
            </button>
          </div>
        </StyledModal>
      )}
    </div>
  );
}

UserListViewModern.propTypes = {
  users: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  userDocuments: PropTypes.array,
  auth: PropTypes.object.isRequired,
  onViewUser: PropTypes.func.isRequired,
  onInviteUser: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  showDeleteModal: PropTypes.bool.isRequired,
  confirmDeleteUser: PropTypes.func.isRequired,
  cancelDeleteUser: PropTypes.func.isRequired,
};

export default UserListViewModern;

